import React, { useState, useEffect, useMemo } from "react";
import ChatWindow from "../ChatWindow/ChatWindow";
import { useChat } from "./ChatProvider/ChatProvider";
import MessageInput from "../MessageInput/MessageInput";
import "./Layout.css";
import Sidebar from "../SideBar/SideBar";
import { getServerIp } from "../../../services/api";
import { jwtDecode } from "jwt-decode";

interface TokenPayload {
    exp: number;
    business_type_id: number;
    is_message_input?: boolean;
}

const filterLabels = [
    { key: "התראות", className: "filter-alert" },
    { key: "הודעות מאושרות", className: "filter-approved" },
    { key: "הודעות לא רלוונטיות", className: "filter-not-relevant" },
    { key: "הודעות לבדיקה", className: "filter-pending" },
    { key: "הכל", className: "selected" }
];

const Layout: React.FC = () => {
    const [currentDate, setCurrentDate] = useState(getFormattedDate());
    const [serverIp, setServerIp] = useState<string | null>(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isMessageInputAllowed, setIsMessageInputAllowed] = useState(false);
    const { company, messages } = useChat();
    const [selectedFilters, setSelectedFilters] = useState<string[]>(["הכל"]);

    const filteredMessages = useMemo(() => {
        if (selectedFilters.includes("הכל")) {
            return messages;
        }

        return messages.filter(message => {
            const eventStatus = message.metadata?.event_status?.toLowerCase();
            const eventType = message.metadata?.type?.toLowerCase();
            
            if (selectedFilters.includes("התראות") && eventType === "alert") {
                return true;
            }
            if (selectedFilters.includes("הודעות מאושרות") && eventStatus === "confirmed") {
                return true;
            }
            if (selectedFilters.includes("הודעות לא רלוונטיות") && eventStatus === "unrelevant") {
                return true;
            }
            if (selectedFilters.includes("הודעות לבדיקה") && eventStatus === "new") {
                return true;
            }
            return false;
        });
    }, [messages, selectedFilters]);

    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem("authToken");
            if (!token) return;

            try {
                const decoded = jwtDecode<TokenPayload>(token);
                const now = Date.now() / 1000;
                const expiresAt = decoded.exp;
                const isValid = expiresAt - 300 > now; // 5 min buffer
                const hasValidType = decoded.business_type_id === 1;

                setIsAuthorized(isValid && hasValidType);
                //setIsMessageInputAllowed(!!decoded.is_message_input);
            } catch (error) {
                console.error("Invalid token:", error);
                setIsAuthorized(false);
                //setIsMessageInputAllowed(false);
            }

            //TODO - REMOVE
            setIsMessageInputAllowed(true);
        };

        checkAuth();
    }, []);

    useEffect(() => {
        const fetchIp = async () => {
            const ip = await getServerIp();
            setServerIp(ip);
        };

        fetchIp();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentDate(getFormattedDate());
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    function getFormattedDate() {
        return new Date().toLocaleDateString("he-IL", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric"
        });
    }

    const handleFilterClick = (key: string) => {
        if (key === "הכל") {
            setSelectedFilters(["הכל"]);
        } else {
            let newFilters = selectedFilters.includes(key)
                ? selectedFilters.filter(f => f !== key)
                : [...selectedFilters.filter(f => f !== "הכל"), key];
            if (newFilters.length === 0) {
                newFilters = ["הכל"];
            }
            setSelectedFilters(newFilters);
            
            
        }
    };

    if (!isAuthorized) {
        return (
            <div className="unauthorized">
                ⛔ Unauthorized
                <br />
                <button
                    onClick={() => {
                        localStorage.removeItem("authToken");
                        window.location.href = "/";
                    }}
                    style={{
                        marginTop: "1rem",
                        padding: "8px 16px",
                        cursor: "pointer",
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "4px"
                    }}
                >
                    Login
                </button>
            </div>
        );
    }

    return (
        <div className="layout-container">
            <div className="chat-area">
                <div className="chat-area-header">
                    {serverIp && <span className="server-ip">{serverIp}</span>}
                    <div className="header-filters-bar">
                        {filterLabels.map(label => {
                            const isSelected = selectedFilters.includes(label.key);
                            const isAll = label.key === "הכל";
                            const allUnselected = isAll && (!isSelected && selectedFilters.some(f => f !== "הכל"));
                            return (
                                <div
                                    key={label.key}
                                    className={`filter-label ${label.className} ${isSelected ? "selected" : ""} ${allUnselected ? "all-unselected" : ""}`}
                                    onClick={() => handleFilterClick(label.key)}
                                >
                                    {label.key}
                                    {label.key !== "הכל" && (
                                        <span className={`filter-dot dot-${label.className.replace("filter-", "")}`}></span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="company-name-and-date-container">
                        {company?.name && (
                            <a
                                href={`https://platform.openai.com/playground/assistants?assistant=asst_G8jtnkK8BNEZIyCfQIkZc24I&thread=${company.thread_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="company-name-link"
                            >
                                <span className="company-name">
                                    {company?.name || ""} {company?.organization_number ? `- ${company.organization_number}` : ""}
                                </span>
                            </a>
                        )}
                        <span className="current-date">{currentDate}</span>
                    </div>
                </div>
                <ChatWindow messages={filteredMessages} />
                {isMessageInputAllowed && <MessageInput />}
            </div>
            <Sidebar />
        </div>
    );
};

export default Layout;